/**
 * Multi-VAT Helper-Funktionen — zentrale Wahrheit für USt-Berechnung.
 *
 * Feb 2026 — P0-Fix Multi-VAT-Workflow im SB-Portal.
 * Mirror der Backend-Logik (`app/services/vat_calc.py` + `app/routes/closure.py:_expense_vat_total`).
 *
 * REGEL (3-stufige Priorität):
 *   1. tax_breakdown[] mit ≥2 Einträgen → Σ aus breakdown (Multi-Tax-Wahrheit)
 *   2. Explizites vat_amount-Feld → diesen Wert nehmen
 *   3. Auto-Compute aus amount (Brutto) + vat_rate
 *
 * Wer nutzt das?
 *   - ItemDetailDrawer.jsx     (SB-Seite, Drawer-KPI-Karten)
 *   - AdvisorClientDetail.js   (SB-Seite, Aggregat-Summen)
 *   - ExpenseDetail.jsx        (FL-Seite, optional Konsistenz)
 *   - Dashboard-Widgets        (FL-Seite, Cashflow-KPIs)
 */

/**
 * Liefert die Gesamt-USt einer Ausgabe als Number (immer ≥ 0).
 */
export function getExpenseVatTotal(e) {
  if (!e || typeof e !== 'object') return 0;

  // 1) Multi-Tax-Breakdown hat Vorrang
  const breakdown = Array.isArray(e.tax_breakdown) ? e.tax_breakdown : [];
  if (breakdown.length >= 2) {
    let total = 0;
    for (const row of breakdown) {
      const v = parseFloat(row?.vat);
      if (Number.isFinite(v)) total += v;
    }
    return round2(total);
  }

  // 2) Explizites vat_amount-Feld
  const explicit = e.vat_amount;
  if (explicit !== null && explicit !== undefined && explicit !== '') {
    const v = parseFloat(explicit);
    if (Number.isFinite(v) && v > 0) return round2(v);
  }

  // 3) Auto-Compute aus amount + vat_rate
  const amt = parseFloat(e.amount);
  const rate = parseFloat(e.vat_rate);
  if (Number.isFinite(amt) && amt > 0 && Number.isFinite(rate) && rate > 0) {
    const net = (amt * 100) / (100 + rate);
    return round2(amt - net);
  }

  return 0;
}

/**
 * Liefert die Gesamt-Netto-Summe einer Ausgabe (Brutto − USt-Gesamt).
 */
export function getExpenseNetTotal(e) {
  if (!e || typeof e !== 'object') return 0;
  const brutto = parseFloat(e.amount) || 0;
  return round2(brutto - getExpenseVatTotal(e));
}

/**
 * Liefert die anzuzeigenden MwSt-Sätze einer Ausgabe für Labels/Badges.
 * Bei Multi-Tax: ["7%", "19%"]. Bei Single: ["19%"]. Bei keinem: [].
 */
export function getDisplayRates(e) {
  if (!e || typeof e !== 'object') return [];

  const breakdown = Array.isArray(e.tax_breakdown) ? e.tax_breakdown : [];
  if (breakdown.length >= 2) {
    const rates = breakdown
      .map(r => parseFloat(r?.rate))
      .filter(r => Number.isFinite(r))
      .sort((a, b) => a - b);
    // Duplikate entfernen
    const uniq = [...new Set(rates)];
    return uniq.map(r => `${r % 1 === 0 ? r.toFixed(0) : r}%`);
  }

  const rate = parseFloat(e.vat_rate);
  if (Number.isFinite(rate) && rate > 0) {
    return [`${rate % 1 === 0 ? rate.toFixed(0) : rate}%`];
  }
  return [];
}

/**
 * True, wenn die Ausgabe Multi-Tax ist (mindestens 2 echte Sätze im Breakdown).
 */
export function isMultiTax(e) {
  if (!e || typeof e !== 'object') return false;
  const breakdown = Array.isArray(e.tax_breakdown) ? e.tax_breakdown : [];
  return breakdown.length >= 2;
}

/**
 * Liefert USt-Anzeige für eine Rechnung (Einnahme) — Multi-Tax-fähig.
 * Rechnungen sind anders strukturiert als Ausgaben: items[] mit eigenen tax_rates.
 *
 * Reihenfolge:
 *   1. tax_total-Feld (zuverlässig wenn vom Backend berechnet)
 *   2. items[]-Aggregation
 */
export function getInvoiceVatTotal(inv) {
  if (!inv || typeof inv !== 'object') return 0;
  const explicit = parseFloat(inv.tax_total);
  if (Number.isFinite(explicit) && explicit > 0) return round2(explicit);

  // Fallback aus items
  const items = Array.isArray(inv.items) ? inv.items : [];
  let total = 0;
  for (const it of items) {
    const itemNet = parseFloat(it?.total ?? it?.net) || 0;
    const rate = parseFloat(it?.tax_rate ?? it?.vat_rate) || 0;
    total += (itemNet * rate) / 100;
  }
  return round2(total);
}

/**
 * Liefert die anzuzeigenden Steuersätze einer Rechnung (items[] aggregiert).
 */
export function getInvoiceDisplayRates(inv) {
  if (!inv || typeof inv !== 'object') return [];
  const items = Array.isArray(inv.items) ? inv.items : [];
  const rates = items
    .map(it => parseFloat(it?.tax_rate ?? it?.vat_rate))
    .filter(r => Number.isFinite(r));
  const uniq = [...new Set(rates)].sort((a, b) => a - b);
  return uniq.map(r => `${r % 1 === 0 ? r.toFixed(0) : r}%`);
}

/**
 * True, wenn die Rechnung Multi-Tax-Items hat.
 */
export function isInvoiceMultiTax(inv) {
  return getInvoiceDisplayRates(inv).length >= 2;
}

function round2(n) {
  return Math.round((Number(n) + Number.EPSILON) * 100) / 100;
}
